import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";

import { BODY_SMALL } from "@/components/marketing/marketingType";
import {
  ACCENT_DEEP,
  NOT_WIDE,
  TINY,
  WIDE,
  marketingPalette,
} from "@/components/marketing/tokens";
import type { Card } from "@/content/landing/types";
import { LandingImage, LandingSection, LinkedText } from "../LandingPrimitives";
import { BandHeading, type Cards } from "./cardsShared";
import { Lines } from "@/components/marketing/Lines";
import { RichParagraph } from "./splitText";

/**
 * The tinted grounds the "why users leave" cards are drawn on, in the order
 * every published page runs them: green, orange, blue, then red. A fact about
 * the design, so it lives here and not on the cards.
 */
const TONES = ["#E2F6E0", "#FFF1E5", "#F2F2FD", "#FFE4E5"];

/** Both columns take the same measure, and both stack at the wide step. */
const COLUMN = {
  width: 1 / 2,
  display: "flex",
  flexDirection: "column",
  paddingTop: "100px",
  paddingBottom: "100px",
  [NOT_WIDE]: { width: 1, paddingBottom: 0 },
} satisfies SxProps<Theme>;

/**
 * `compliance-bullet_item-2`: a dot and a line, not the site's check glyph.
 * The line links the card's phrases as its paragraphs do.
 */
function DotLine({ label, links }: { label: string; links: Card["links"] }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: "10.08px",
        fontSize: 15,
        lineHeight: "22.5px",
        color: marketingPalette.ink,
      }}
    >
      <Box
        sx={{
          width: "6.4px",
          height: "8px",
          mt: "8.48px",
          borderRadius: "1440px",
          backgroundColor: ACCENT_DEEP,
        }}
      />
      <Box>
        <LinkedText text={label} links={links} />
      </Box>
    </Box>
  );
}

function ToneCard({ card, at }: { card: Card; at: number }) {
  const [lead, ...rest] = card.paragraphs ?? [];
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        p: "24px",
        borderRadius: "16px",
        border: `4px solid ${marketingPalette.surface}`,
        backgroundColor: TONES[at % TONES.length],
        // The resting place steps 30 per card, under the heading's own 100.
        [WIDE]: { position: "sticky", top: `${120 + 30 * at}px` },
        [TINY]: { p: "16px" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "16px" }}>
        {card.icon ? (
          <LandingImage
            src={card.icon}
            sx={{ height: "48px", width: "48px", flexShrink: 0 }}
          />
        ) : null}
        <Typography
          // `h5_new`: an h5, not an h3, which would add an anchor the
          // published outline does not have.
          component="h5"
          sx={{
            fontSize: 22,
            fontWeight: 500,
            lineHeight: "30px",
            letterSpacing: "-0.66px",
          }}
        >
          <Lines text={card.title} />
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: "16px",
        }}
      >
        {lead ? (
          <RichParagraph text={lead} links={card.links} sx={BODY_SMALL} />
        ) : null}
        {card.bullets?.length ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            {card.bullets.map((bullet, index) => (
              <DotLine key={index} label={bullet} links={card.links} />
            ))}
          </Box>
        ) : null}
        {rest.map((paragraph, index) => (
          <RichParagraph
            key={index}
            text={paragraph}
            links={card.links}
            sx={BODY_SMALL}
          />
        ))}
      </Box>
    </Box>
  );
}

/**
 * `alternative-scrol-wrap`: a two-column band, not a grid of cards under a
 * heading. The heading holds the left half and stays put while the tinted
 * cards stack past it down the right.
 */
export default function CardsTone({ block }: { block: Cards }) {
  return (
    // `.container_new.padding-bottom`: the section's short close.
    <LandingSection ground="grey" padding="break">
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          position: "relative",
          [NOT_WIDE]: { flexDirection: "column" },
        }}
      >
        <Box
          sx={[
            COLUMN,
            {
              gap: "16px",
              justifyContent: "center",
              [WIDE]: { position: "sticky", top: "100px" },
              [NOT_WIDE]: { paddingTop: "64px" },
              [TINY]: { paddingTop: "48px" },
            },
          ]}
        >
          <BandHeading block={block} gap="16px" align="left" />
        </Box>
        <Box
          sx={[
            COLUMN,
            { gap: "32px", [NOT_WIDE]: { gap: "16px", paddingTop: "32px" } },
          ]}
        >
          {block.items.map((card, index) => (
            <ToneCard key={card.title} card={card} at={index} />
          ))}
        </Box>
      </Box>
    </LandingSection>
  );
}
